import React from "react";
import { Box, Grid, Typography } from "@mui/material";

interface UvodSectionProps {
  saleDates: string[];
}

const stats = [
  { value: "80 ha", label: "obhospodařované půdy" },
  { value: "500 m", label: "nadmořská výška" },
  { value: "2009", label: "rok založení" },
  { value: "BIO", label: "certifikovaný chov" },
];

const parseDate = (dateStr: string): Date | null => {
  const stripped = dateStr.replace(/[()]/g, "");
  const match = stripped.match(/(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})/);
  if (!match) return null;
  return new Date(parseInt(match[3]), parseInt(match[2]) - 1, parseInt(match[1]));
};

const UvodSection: React.FC<UvodSectionProps> = ({ saleDates }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const upcomingDates = saleDates
    .filter((date) => {
      const d = parseDate(date);
      return d !== null && d >= today;
    })
    .slice(0, 3);

  return (
    <Box id="uvod" sx={{ background: "#faf5ec" }}>

      {/* Main intro */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, md: 6 }, maxWidth: "950px", mx: "auto" }}>
        <Typography
          sx={{ fontSize: "13px", letterSpacing: "4px", color: "#a89858", textTransform: "uppercase", mb: 1 }}
        >
          Úvod
        </Typography>
        <Typography variant="h4" component="h2" sx={{ color: "#2d2a22", fontWeight: 700, mb: 3, maxWidth: "640px" }}>
          Náš statek
        </Typography>
        <Typography sx={{ color: "#6a5a45", maxWidth: "950px", lineHeight: 1.85, fontSize: "16px", mb: 2 }}>
          Náš rodinný statek se nachází v obci Lipí u Náchoda. Od roku 2009 se zabýváme chovem masného skotu plemene
          Blonde d'Aquitaine v kontrolovaném BIO režimu. Hospodaříme na cca 80 ha půdy v nadmořské výšce kolem 500 m.
        </Typography>
        <Typography sx={{ color: "#6a5a45", maxWidth: "950px", lineHeight: 1.85, fontSize: "16px" }}>
          Zvířata mají během celého vegetačního období přístup na pastviny. Klademe důraz na dostatek péče,
          způsob výživy, volnost pohybu a soulad s přírodou — matky s telátky tráví celé léto na pastvině.
        </Typography>

        {/* Stats row */}
        <Grid container spacing={2} sx={{ mt: 5, maxWidth: "950px" }}>
          {stats.map((s) => (
            <Grid item xs={6} key={s.label} sx={{ "@media (min-width: 550px)": { flexBasis: "25%", maxWidth: "25%" } }}>
              <Box
                sx={{
                  p: 2.5,
                  background: "white",
                  borderRadius: "10px",
                  border: "1px solid #e8d8c0",
                  textAlign: "center",
                }}
              >
                <Typography sx={{ fontSize: "22px", fontWeight: 700, color: "#2d2a22", lineHeight: 1 }}>
                  {s.value}
                </Typography>
                <Typography sx={{ fontSize: "11px", color: "#9a8a72", mt: 0.5, lineHeight: 1.3 }}>
                  {s.label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Sale dates banner */}
      <Box sx={{ px: { xs: 2, md: 6 }, mb: { xs: 6, md: 10 }, maxWidth: "950px", mx: "auto" }}>
      <Box
        sx={{
          background: "#2d2a22",
          borderRadius: "14px",
          overflow: "hidden",
          maxWidth: "860px",
        }}
      >
        <Box
          sx={{
            px: { xs: 3, md: 5 },
            py: 3,
            borderBottom: "1px solid rgba(200,184,122,0.2)",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: "#c8b87a",
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{ color: "#c8b87a", fontSize: "11px", letterSpacing: "4px", textTransform: "uppercase" }}
          >
            Termíny prodeje
          </Typography>
        </Box>

        <Box sx={{ px: { xs: 3, md: 5 }, py: { xs: 3, md: 4 }, display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 4 }}>
          {/* Info */}
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: "#f5edd8", fontSize: "16px", fontWeight: 600, mb: 1 }}>
              Přibližně jednou za 1 - 2 měsíce vždy v sobotu
            </Typography>
            <Typography sx={{ color: "#a09070", fontSize: "14px", lineHeight: 1.7 }}>
              Volný prodej steaků probíhá přímo na statku. Smíšený balíček je možný
              pouze po předchozím objednání na telefonu.
            </Typography>
          </Box>

          {/* Dates */}
          <Box sx={{ flexShrink: 0 }}>
            <Typography
              sx={{ color: "#c8b87a", fontSize: "11px", letterSpacing: "3px", textTransform: "uppercase", mb: 1.5 }}
            >
              Nejbližší termíny
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {upcomingDates.map((date) => (
                <Box
                  key={date}
                  sx={{
                    px: 2.5,
                    py: 1,
                    background: "rgba(200,184,122,0.12)",
                    border: "1px solid rgba(200,184,122,0.25)",
                    borderRadius: "6px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: "#c8b87a", flexShrink: 0 }} />
                  <Typography sx={{ color: "#f5edd8", fontSize: "15px", fontWeight: 600 }}>
                    {date}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
      </Box>

    </Box>
  );
};

export default UvodSection;
